import { Path, Svg } from "react-native-svg";

import { useColorScheme } from "@/theme/colorScheme";
import {
  GEM_ASPECT,
  GEM_BLADE_MIN_PX,
  GEM_BLADES,
  GEM_BODY,
  GEM_COLORS,
  GEM_VIEWBOX,
} from "./gem-geometry";

export type GemMarkProps = {
  /** The mark's height in px; the width follows the 189:148 aspect. */
  size?: number;
  /**
   * Paints blades and body in one color instead of the theme pairing. The named exception to that
   * pairing is the shared QR story lockup, whose ground is baked into a PNG and can itself be
   * Signal Indigo. Do not widen that use without DESIGN.md §7.
   */
  color?: string;
};

/**
 * The Trovie gem mark: a flat cut gem holding a T, tinted from the active theme so the blades
 * carry the Signal hue and the body carries the ink.
 *
 * The flat mark (ADR-0046) is static and is not one of the brand layer's named motion exceptions,
 * so this renders no animation and takes no press.
 *
 * Hidden from assistive tech: every placement sits beside text that already names the context (the
 * wordmark, an empty state's title, a price row's source name), so the mark adds nothing to hear.
 * A future standalone use that must be announced should wrap it in a labelled element.
 */
export function GemMark({ size = 32, color }: GemMarkProps) {
  const { colorScheme } = useColorScheme();
  const { blade, body } = GEM_COLORS[colorScheme === "dark" ? "dark" : "light"];
  return (
    <Svg
      width={Math.round(size * GEM_ASPECT)}
      height={size}
      viewBox={GEM_VIEWBOX}
      aria-hidden
    >
      {size >= GEM_BLADE_MIN_PX
        ? GEM_BLADES.map((d, i) => <Path key={i} d={d} fill={color ?? blade} />)
        : null}
      {GEM_BODY.map((d, i) => <Path key={i} d={d} fill={color ?? body} />)}
    </Svg>
  );
}
